// InShellUsageMeter — live usage tracker for the chat composer footer.
//
// Surfaces three signals so the user can see what every send "costs"
// without leaving the chat:
//   1. This conversation's turn count (user messages only).
//   2. Today's chat usage from `daily_usage` (resets at UTC midnight).
//   3. This month's credit budget from `check-credit-limit` — used /
//      limit + a slim bar, color-shifts when over the warn threshold.
//
// Refreshes whenever `turnCount` ticks (passed from UnifiedChatShell so
// every new send re-reads the counters) and on auth change. Stays
// quiet for signed-out users — they get the rotating CreditBurnTicker
// instead, which already handles the unauth case.
import { useEffect, useRef, useState } from "react";
import { Activity, Gauge, MessageSquare, ArrowRight, ExternalLink, CalendarClock, RefreshCw, Copy, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useDailyUsage } from "@/hooks/useDailyUsage";
import { useCreditLimit } from "@/hooks/useCreditLimit";
import { useTimezonePref, formatInTz, getTzAbbr, TZ_AUTO, resolveTz } from "@/hooks/useTimezonePref";
import { buildTzOptionGroups } from "@/lib/tzOptions";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import CreditsUsageCard from "@/components/account/CreditsUsageCard";
import { cn } from "@/lib/utils";
import {
  computeResetAt,
  msUntilReset as msUntilResetFn,
  breakdownUntilReset,
  isClockSkewed,
  RESET_SKEW_LABEL,
} from "@/lib/creditReset";

interface Props {
  /** Number of user-sent turns in the current conversation. */
  turnCount: number;
  /**
   * Number of *completed* assistant turns (streaming finished). We refresh
   * usage + credits when this ticks because that's the moment the backend
   * has actually debited the spend — refreshing on send alone shows stale
   * numbers (the assistant reply hasn't been billed yet).
   */
  completedAssistantCount?: number;
}

const InShellUsageMeter = ({ turnCount, completedAssistantCount = 0 }: Props) => {
  const { user } = useAuth();
  const { usage, refresh: refreshUsage } = useDailyUsage();
  const { data: credit, refresh: refreshCredit, pct } = useCreditLimit();
  // Honor the user's timezone preference (Settings → Timezone). Defaults
  // to "auto" which resolves to the browser zone — keeps the displayed
  // reset time understandable instead of always-UTC.
  const { tz, setTz } = useTimezonePref();
  const tzOptionGroups = buildTzOptionGroups(resolveTz(TZ_AUTO));
  // Reset clock — credits roll over at 00:00 UTC on the 1st of next month
  // (see `get_monthly_credits` SQL helper). We re-derive once a minute so
  // the "in 3d 4h" string stays accurate without polling the backend.
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  // Briefly highlight the credit + today numbers right after a refresh so
  // the user *sees* the update happen instead of having to spot a digit
  // change in mono text.
  const [pulse, setPulse] = useState(false);
  const pulseTimer = useRef<number | null>(null);
  const prevUsed = useRef<number>(0);
  const prevToday = useRef<number>(0);
  // Manual refresh state for the "Refresh now" control inside the sheet.
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const handleManualRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await Promise.all([
        Promise.resolve(refreshUsage()),
        Promise.resolve(refreshCredit()),
      ]);
      setLastRefreshedAt(new Date());
      // Trigger pulse so the user *sees* the values reload.
      setPulse(true);
      if (pulseTimer.current) window.clearTimeout(pulseTimer.current);
      pulseTimer.current = window.setTimeout(() => setPulse(false), 1200);
    } finally {
      // Keep the spinner visible briefly so the action feels real even
      // when the underlying refresh resolves instantly from cache.
      window.setTimeout(() => setRefreshing(false), 400);
    }
  };

  // Re-render the "just updated" relative timestamp once a minute.
  useEffect(() => {
    if (!lastRefreshedAt) return;
    const id = window.setInterval(() => setRefreshTick((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, [lastRefreshedAt]);

  const lastRefreshedLabel = (() => {
    if (!lastRefreshedAt) return null;
    // Reference refreshTick so the label re-derives on the interval.
    void refreshTick;
    const diffSec = Math.max(0, Math.floor((Date.now() - lastRefreshedAt.getTime()) / 1000));
    if (diffSec < 5) return "just now";
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    return `${diffHr}h ago`;
  })();

  // Refresh on send (optimistic — shows the new chat-count quickly) AND
  // again when a reply finishes streaming (the moment credits actually
  // get debited). Both ticks are cheap; the hooks no-op when signed out.
  useEffect(() => {
    if (turnCount === 0 && completedAssistantCount === 0) return;
    refreshUsage();
    refreshCredit();
  }, [turnCount, completedAssistantCount, refreshUsage, refreshCredit]);

  // When either counter actually moves, flash the pulse for ~1.2s.
  const usedNow = credit?.used ?? 0;
  const todayNow = usage.chat;
  useEffect(() => {
    if (usedNow !== prevUsed.current || todayNow !== prevToday.current) {
      // Skip the very first render so we don't pulse on initial load.
      if (prevUsed.current !== 0 || prevToday.current !== 0) {
        setPulse(true);
        if (pulseTimer.current) window.clearTimeout(pulseTimer.current);
        pulseTimer.current = window.setTimeout(() => setPulse(false), 1200);
      }
      prevUsed.current = usedNow;
      prevToday.current = todayNow;
    }
  }, [usedNow, todayNow]);

  useEffect(() => () => {
    if (pulseTimer.current) window.clearTimeout(pulseTimer.current);
  }, []);

  // Last-rollover tracking — persist the most recent monthly reset moment
  // we've observed for this user in localStorage, so the transparency
  // panel can show "Last rollover: Xd ago" without round-tripping the
  // backend. Hooks must run unconditionally, so we declare state +
  // effect here (above the early return) and derive display values
  // further down once `user` is confirmed present.
  const [storedRolloverIso, setStoredRolloverIso] = useState<string | null>(
    null,
  );
  // Tracks the brief "Copied!" affordance on the ISO 8601 copy button —
  // flips back to the copy icon ~1.5s after a successful clipboard write
  // so the panel doesn't get stuck in the success state.
  const [copiedIso, setCopiedIso] = useState(false);
  // Reset-details panel timezone view. "both" stacks the user's TZ
  // and raw UTC side-by-side so support / power-users can sanity-check
  // the meter without flipping a setting; "tz" and "utc" collapse to
  // a single column for a cleaner read. Default to "both" because the
  // whole point of this panel is full transparency.
  const [tzView, setTzView] = useState<"both" | "tz" | "utc">("both");
  // Debug mode — opt-in toggle that surfaces the raw resetAt /
  // msUntilReset / breakdownUntilReset inputs and outputs in the
  // refill-details panel. Persisted per-browser so a power-user or
  // support agent who flipped it on stays in debug across reloads
  // without re-toggling. Defaults to off so the panel stays calm
  // for everyday users.
  const DEBUG_STORAGE_KEY = "hl.refillDebugMode";
  const [debugMode, setDebugMode] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(DEBUG_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(DEBUG_STORAGE_KEY, debugMode ? "1" : "0");
    } catch {
      // localStorage may be blocked (private mode, quota). Non-fatal —
      // the toggle still works for the lifetime of this tab.
    }
  }, [debugMode]);
  // Recompute the start-of-current-UTC-month moment off the same `now`
  // tick that drives the rest of the meter so the effect re-fires when
  // the calendar month flips in a long-lived session.
  const currentRolloverIso = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0),
  ).toISOString();
  const rolloverStorageKey = user ? `hl.lastResetAt.${user.id}` : null;
  useEffect(() => {
    if (!rolloverStorageKey) {
      setStoredRolloverIso(null);
      return;
    }
    try {
      const existing = window.localStorage.getItem(rolloverStorageKey);
      if (existing !== currentRolloverIso) {
        window.localStorage.setItem(rolloverStorageKey, currentRolloverIso);
        setStoredRolloverIso(currentRolloverIso);
      } else {
        setStoredRolloverIso(existing);
      }
    } catch {
      // localStorage may be blocked (private mode, quota). Fall back
      // to the in-memory derivation — still accurate, non-persistent.
      setStoredRolloverIso(currentRolloverIso);
    }
  }, [rolloverStorageKey, currentRolloverIso]);

  if (!user) return null;

  const limit = credit?.limit ?? null;
  const used = credit?.used ?? 0;
  const isUnlimited = limit === null;
  const overWarn = !isUnlimited && pct >= 0.8;
  const overCap = !isUnlimited && pct >= 1;
  const barTone = overCap
    ? "bg-destructive"
    : overWarn
      ? "bg-amber-400"
      : "bg-primary";
  const textTone = overCap
    ? "text-destructive"
    : overWarn
      ? "text-amber-400"
      : "text-muted-foreground";

  // Next reset = 1st of next month at 00:00 UTC. Format as a short
  // "in Xd Yh" string for the inline meter and a full UTC timestamp
  // for the tooltip + sheet panel.
  // Delegated to `lib/creditReset` so the same formula can be unit-tested
  // for end-of-month + DST edge cases (see creditReset.test.ts).
  const resetAt = computeResetAt(now);
  const msUntilReset = msUntilResetFn(now, resetAt);
  // Defensive — `computeResetAt(now)` always returns a future moment
  // relative to the same `now`, so this should be false in practice.
  // It guards against future callers passing a memoised / cached
  // `resetAt` that's drifted past the local clock (see
  // `isClockSkewed` docs in lib/creditReset.ts).
  const skewed = isClockSkewed(now, resetAt);
  const daysUntilReset = Math.floor(msUntilReset / 86_400_000);
  const hoursUntilReset = Math.floor(
    (msUntilReset % 86_400_000) / 3_600_000,
  );
  // When skewed, every downstream display becomes the same friendly
  // fallback. Keeping the variable shape stable (still a string) means
  // every consumer below — tooltip, sheet header, refill-window
  // banner — gets the safe value with zero extra branching.
  const resetCompact = skewed
    ? RESET_SKEW_LABEL
    : daysUntilReset >= 1
      ? `${daysUntilReset}d ${hoursUntilReset}h`
      : `${hoursUntilReset || 1}h`;
  const resetFull = formatInTz(resetAt, tz, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  // Same moment, formatted in raw UTC — used by the side-by-side
  // toggle in the reset-details panel so power users can audit the
  // canonical time alongside their local clock.
  const resetFullUtc = formatInTz(resetAt, "UTC", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  const minutesUntilReset = Math.floor(
    (msUntilReset % 3_600_000) / 60_000,
  );
  // Short label for the user's preferred zone at the refill moment
  // (e.g. "PDT", "UTC+2"). Surfaced next to the compact countdown so
  // a user reading "refills in 3d 2h" instantly knows which clock the
  // countdown is anchored to without expanding the details panel.
  const resetTzAbbr = getTzAbbr(tz, resetAt);
  const resetTitle = `Monthly credits refill on ${resetFull} (in ${resetCompact} ${resetTzAbbr} time)`;

  // "Reset window" — the final 24 hours before monthly credits roll
  // over. Surfaces a calm but visible highlight so the user sees the
  // refill coming and can plan whether to spend remaining credits or
  // hold off. Capped at 24h so it doesn't bleed into normal weeks.
  const resetSoon = msUntilReset > 0 && msUntilReset <= 86_400_000;

  // Derive display values for the persisted last-rollover moment. The
  // hooks themselves live above the `if (!user) return null` guard.
  const lastRolloverDate = new Date(storedRolloverIso ?? currentRolloverIso);
  const msSinceRollover = Math.max(
    0,
    now.getTime() - lastRolloverDate.getTime(),
  );
  const daysSinceRollover = Math.floor(msSinceRollover / 86_400_000);
  const hoursSinceRollover = Math.floor(
    (msSinceRollover % 86_400_000) / 3_600_000,
  );
  const sinceRolloverLabel =
    daysSinceRollover >= 1
      ? `${daysSinceRollover}d ${hoursSinceRollover}h ago`
      : hoursSinceRollover >= 1
        ? `${hoursSinceRollover}h ago`
        : "less than an hour ago";
  const lastRolloverFull = formatInTz(lastRolloverDate, tz, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          className="group flex items-center gap-3 text-[10px] font-mono text-muted-foreground rounded px-1 -mx-1 py-0.5 hover:bg-secondary/60 hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label="Open credits & billing details"
          title="Open credits & billing details"
        >
      <span className="inline-flex items-center gap-1" title="Messages you've sent in this conversation">
        <MessageSquare className="w-3 h-3" />
        <span className="tabular-nums">{turnCount}</span>
        <span className="opacity-70">turn{turnCount === 1 ? "" : "s"}</span>
      </span>
      <span aria-hidden className="opacity-30">·</span>
      <span className="inline-flex items-center gap-1" title="Chat messages used today (resets at UTC midnight)">
        <Activity className="w-3 h-3" />
        <span
          className={cn(
            "tabular-nums transition-colors duration-300",
            pulse && "text-primary",
          )}
        >
          {usage.chat}
        </span>
        <span className="opacity-70">today</span>
      </span>
      <span aria-hidden className="opacity-30">·</span>
      <span
        className={cn(
          "inline-flex items-center gap-1.5 min-w-0 transition-colors duration-300",
          textTone,
          pulse && !overCap && !overWarn && "text-primary",
        )}
        title={
          isUnlimited
            ? `${used.toLocaleString()} credits this month — unlimited on your plan, no monthly refill needed`
            : `${used.toLocaleString()} of ${limit?.toLocaleString()} credits used this month (${Math.round(pct * 100)}%)`
        }
      >
        <Gauge className="w-3 h-3" />
        <span
          className={cn(
            "tabular-nums transition-transform duration-300",
            pulse && "scale-110",
          )}
        >
          {used.toLocaleString()}
          {isUnlimited ? "" : `/${limit?.toLocaleString()}`}
        </span>
        <span className="opacity-70">
          {isUnlimited ? "credits · unlimited" : "credits"}
        </span>
        {!isUnlimited && (
          <span
            className="ml-1 h-1 w-12 rounded-full bg-secondary overflow-hidden"
            aria-hidden
          >
            <span
              className={cn("block h-full transition-[width] duration-500", barTone)}
              style={{ width: `${Math.min(100, Math.round(pct * 100))}%` }}
            />
          </span>
        )}
      </span>
          {!isUnlimited && (
            <>
              <span aria-hidden className="opacity-30">·</span>
              <span
                className={cn(
                  "inline-flex items-center gap-1 transition-colors",
                  resetSoon &&
                    "px-1.5 py-0.5 -my-0.5 rounded-full bg-primary/10 text-primary ring-1 ring-primary/30 animate-pulse",
                )}
                title={
                  resetSoon
                    ? `Refill window: monthly credits refill in ${resetCompact} (${resetFull})`
                    : resetTitle
                }
              >
                <CalendarClock className="w-3 h-3" />
                <span className={cn("opacity-70", resetSoon && "opacity-90")}>
                  refills in
                </span>
                <span className="tabular-nums">{resetCompact}</span>
                <span
                  className="opacity-60 text-[9px] uppercase tracking-wider"
                  aria-label={`anchored to ${resetTzAbbr}`}
                  title={`Countdown anchored to ${resetTzAbbr} (your preferred timezone)`}
                >
                  {resetTzAbbr}
                </span>
              </span>
            </>
          )}
          <ArrowRight
            aria-hidden
            className="w-3 h-3 opacity-0 -translate-x-1 transition-all group-hover:opacity-60 group-hover:translate-x-0"
          />
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto"
      >
        <SheetHeader className="text-left mb-4">
          <SheetTitle className="font-mono">Credits &amp; billing</SheetTitle>
          <SheetDescription>
            Live snapshot of your monthly credits, daily caps, and plan.
          </SheetDescription>
        </SheetHeader>
        {/* "Reset time in my timezone" preview — pinned to the sheet
            header so the user immediately sees when their credits
            refill and how long that is, without scrolling to the
            details panel. Hidden when the user is on an unlimited
            plan because the countdown isn't meaningful there. */}
        {!isUnlimited && (
          <div
            className="mb-4 rounded-md border border-border bg-card/60 px-3 py-2"
            aria-label="Next monthly credit refill in your timezone"
          >
            <div className="flex items-center gap-2">
              <CalendarClock className="w-3.5 h-3.5 text-primary shrink-0" />
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                Refill time in your timezone
              </p>
            </div>
            <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <p className="font-mono font-semibold text-sm text-foreground break-all">
                {resetFull}
              </p>
              <p className="font-mono text-[11px] text-muted-foreground tabular-nums">
                · in <span className="text-foreground">{resetCompact}</span>{" "}
                <span className="opacity-70">({resetTzAbbr})</span>
              </p>
            </div>
            {/* Inline TZ switcher — saves a trip to /settings when the
                user just wants to sanity-check the refill against a
                different clock (e.g. a teammate's zone). Writes through
                the same `useTimezonePref` hook so the choice persists
                to Postgres for signed-in users and propagates to every
                other timestamp on the page in the same tick. */}
            <div className="mt-2 flex items-center gap-2">
              <label
                htmlFor="tz-switcher-sheet"
                className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground shrink-0"
              >
                Show in
              </label>
              <select
                id="tz-switcher-sheet"
                value={tz}
                onChange={(e) => setTz(e.target.value)}
                aria-label="Display timezone for refill timestamps"
                className="flex-1 min-w-0 bg-secondary border border-border rounded-md px-2 py-1 text-[11px] font-mono text-foreground focus:outline-none focus:border-primary"
              >
                {tzOptionGroups.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {tz !== TZ_AUTO && (
                <button
                  type="button"
                  onClick={() => setTz(TZ_AUTO)}
                  className="shrink-0 px-2 py-1 rounded border border-border text-[10px] font-mono text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
                  aria-label="Reset timezone preference to automatic"
                >
                  Auto
                </button>
              )}
            </div>
          </div>
        )}
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="text-[11px] font-mono text-muted-foreground" aria-live="polite">
            {lastRefreshedLabel ? (
              <>Updated <span className="text-foreground">{lastRefreshedLabel}</span></>
            ) : (
              <span className="opacity-70">Live data</span>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleManualRefresh}
            disabled={refreshing}
            aria-label="Refresh credit balances and caps now"
            className="h-8 gap-1.5 font-mono text-xs"
          >
            <RefreshCw
              className={cn("w-3.5 h-3.5", refreshing && "animate-spin")}
              aria-hidden
            />
            {refreshing ? "Refreshing…" : "Refresh now"}
          </Button>
        </div>
        <CreditsUsageCard />
        {/* Unlimited-plan banner — explicit copy so the refill vocabulary
            stays consistent even when there's nothing to refill. We keep
            the wording symmetrical with the "Monthly credits refill on…"
            line below, which is hidden for unlimited users. */}
        {isUnlimited && (
          <p
            className="mt-3 text-[11px] font-mono text-muted-foreground inline-flex items-center gap-1.5"
            aria-live="polite"
          >
            <CalendarClock className="w-3.5 h-3.5" />
            Unlimited credits on your plan —{" "}
            <span className="text-foreground">no monthly refill needed</span>
          </p>
        )}
        {/* 0-credits / over-cap callout — when the user has burned through
            their monthly allotment we surface the next refill moment in
            the same "refills in / refills at" vocabulary used elsewhere,
            so the empty state is never silent and never says "resets". */}
        {!isUnlimited && overCap && (
          <div
            className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 text-destructive px-3 py-2 text-[11px] font-mono inline-flex items-center gap-2"
            role="status"
            aria-live="polite"
          >
            <CalendarClock className="w-3.5 h-3.5 shrink-0" />
            <span>
              Out of credits —{" "}
              <span className="font-semibold">
                refills in {resetCompact}
              </span>{" "}
              <span className="opacity-80">
                (refills at {resetFull} {resetTzAbbr})
              </span>
            </span>
          </div>
        )}
        {!isUnlimited && resetSoon && (
          <div
            className="mt-4 rounded-md border border-primary/40 bg-primary/10 text-primary px-3 py-2 text-[11px] font-mono inline-flex items-center gap-2"
            role="status"
            aria-live="polite"
          >
            <CalendarClock className="w-3.5 h-3.5 shrink-0 animate-pulse" />
            <span>
              Refill window —{" "}
              <span className="font-semibold">credits refill in {resetCompact}</span>{" "}
              <span className="opacity-80">({resetTzAbbr})</span>
            </span>
          </div>
        )}
        {!isUnlimited && (
          <p className="mt-3 text-[11px] font-mono text-muted-foreground inline-flex items-center gap-1.5">
            <CalendarClock className="w-3.5 h-3.5" />
            Monthly credits refill on{" "}
            <span className="text-foreground">{resetFull}</span>
            <span className="opacity-70">· in {resetCompact} {resetTzAbbr}</span>
          </p>
        )}
        {/* Transparency panel — exposes the exact computed reset moment
            and the cycle basis so power users can audit what the meter
            is counting against. The headline `resetFull` above is in
            the user's chosen timezone; here we also show raw UTC and
            the ISO string so it's unambiguous and copy-pasteable for
            support. */}
        {!isUnlimited && (
          <details className="mt-3 rounded-md border border-border bg-card/40 px-3 py-2 text-[11px] font-mono text-muted-foreground">
            <summary className="cursor-pointer select-none text-foreground/80 hover:text-foreground transition-colors">
              Refill details &amp; cycle basis
            </summary>
            {/* Debug-mode toggle — opt-in surfacing of the raw resetAt /
                msUntilReset / breakdownUntilReset values. Hidden behind
                a button so the panel stays calm for everyday users while
                power-users / support can flip transparency to maximum. */}
            <div className="mt-2 flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase tracking-wider opacity-70">
                Debug mode
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={debugMode}
                aria-label={
                  debugMode
                    ? "Disable refill debug mode"
                    : "Enable refill debug mode"
                }
                onClick={() => setDebugMode((v) => !v)}
                className={cn(
                  "px-2 py-1 rounded font-mono text-[10px] border transition-colors",
                  debugMode
                    ? "bg-primary/15 text-primary border-primary/40"
                    : "bg-card/60 text-muted-foreground border-border hover:text-foreground hover:border-primary/40",
                )}
              >
                {debugMode ? "On" : "Off"}
              </button>
            </div>
            {/* TZ view toggle — defaults to "Both" so the panel earns
                its "transparency" name without forcing the user to
                open settings. Buttons are tabs in everything but
                semantics; we use `aria-pressed` so screen readers
                announce state. */}
            <div
              role="group"
              aria-label="Choose how to display refill times"
              className="mt-2 inline-flex rounded-md border border-border bg-card/60 p-0.5 text-[10px]"
            >
              {([
                ["both", "Both"],
                ["tz", "Your TZ"],
                ["utc", "UTC"],
              ] as const).map(([value, label]) => {
                const active = tzView === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setTzView(value)}
                    aria-pressed={active}
                    className={cn(
                      "px-2 py-1 rounded font-mono transition-colors",
                      active
                        ? "bg-primary/15 text-primary ring-1 ring-primary/30"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
              <dt className="opacity-70">Cycle basis</dt>
              <dd className="text-foreground">Calendar month (UTC)</dd>
              {/* Always-on side-by-side comparison row. The TZ-view
                  toggle below lets users collapse to a single column,
                  but this row stays put so a screenshot of the panel
                  never loses the local↔UTC mapping — the most common
                  thing a support ticket needs to disambiguate. */}
              <dt className="opacity-70">Local vs UTC</dt>
              <dd className="text-foreground">
                <div className="grid sm:grid-cols-2 gap-x-3 gap-y-1 rounded-md border border-border/60 bg-card/40 p-2">
                  <div>
                    <p className="opacity-60 text-[10px] uppercase tracking-wider">
                      Your local time
                    </p>
                    <p className="break-all">{resetFull}</p>
                  </div>
                  <div>
                    <p className="opacity-60 text-[10px] uppercase tracking-wider">
                      UTC
                    </p>
                    <p className="break-all">{resetFullUtc}</p>
                  </div>
                </div>
              </dd>
              {tzView === "both" ? (
                <>
                  <dt className="opacity-70">Refills at</dt>
                  <dd className="text-foreground">
                    <div className="grid sm:grid-cols-2 gap-x-3 gap-y-1">
                      <div>
                        <p className="opacity-60 text-[10px]">Your TZ</p>
                        <p className="break-all">{resetFull}</p>
                      </div>
                      <div>
                        <p className="opacity-60 text-[10px]">UTC</p>
                        <p className="break-all">{resetFullUtc}</p>
                      </div>
                    </div>
                  </dd>
                </>
              ) : tzView === "tz" ? (
                <>
                  <dt className="opacity-70">Refills at (your TZ)</dt>
                  <dd className="text-foreground break-all">{resetFull}</dd>
                </>
              ) : (
                <>
                  <dt className="opacity-70">Refills at (UTC)</dt>
                  <dd className="text-foreground break-all">{resetFullUtc}</dd>
                </>
              )}
              <dt className="opacity-70">ISO 8601 refill time</dt>
              <dd className="text-foreground break-all">
                <span className="inline-flex items-center gap-1.5 flex-wrap">
                  <span>{resetAt.toISOString()}</span>
                  <button
                    type="button"
                    onClick={async () => {
                      const iso = resetAt.toISOString();
                      try {
                        await navigator.clipboard.writeText(iso);
                        setCopiedIso(true);
                        toast.success("ISO 8601 refill time copied");
                        window.setTimeout(() => setCopiedIso(false), 1500);
                      } catch {
                        toast.error("Couldn't copy — clipboard blocked");
                      }
                    }}
                    aria-label="Copy ISO 8601 refill time"
                    title={copiedIso ? "Copied!" : "Copy ISO 8601 refill time"}
                    className="inline-flex items-center justify-center rounded border border-border bg-card/60 hover:bg-card hover:border-primary/40 transition-colors p-1 text-muted-foreground hover:text-foreground"
                  >
                    {copiedIso ? (
                      <Check className="w-3 h-3 text-primary" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                  </button>
                </span>
              </dd>
              <dt className="opacity-70">Time remaining</dt>
              <dd className="text-foreground tabular-nums">
                {tzView === "both" ? (
                  <div className="grid sm:grid-cols-2 gap-x-3 gap-y-1">
                    <div>
                      <p className="opacity-60 text-[10px]">Your TZ clock</p>
                      <p>
                        {daysUntilReset}d {hoursUntilReset}h
                      </p>
                    </div>
                    <div>
                      <p className="opacity-60 text-[10px]">UTC clock</p>
                      <p>
                        {daysUntilReset}d {hoursUntilReset}h {minutesUntilReset}m
                      </p>
                    </div>
                  </div>
                ) : tzView === "utc" ? (
                  <>
                    {daysUntilReset}d {hoursUntilReset}h {minutesUntilReset}m
                  </>
                ) : (
                  <>
                    {daysUntilReset}d {hoursUntilReset}h
                  </>
                )}
              </dd>
              <dt className="opacity-70">Last rollover</dt>
              <dd className="text-foreground break-all">
                {lastRolloverFull}{" "}
                <span className="opacity-70">· {sinceRolloverLabel}</span>
              </dd>
            </dl>
            {/* Compute inputs — exposes the exact raw values the meter
                feeds into `Date.UTC(year, month + 1, 1, 0, 0, 0)` to
                derive the next refill moment. Aimed at support and the
                "wait, why does it say that?" power-user case: every
                line above is downstream of these four numbers, so if
                the headline looks wrong this is what to read. */}
            {debugMode && (
            <div
              className="mt-3 rounded border border-primary/30 bg-card/60 p-2"
              data-testid="refill-debug-panel"
              aria-label="Refill debug inputs and outputs"
            >
              <p className="opacity-70 mb-1.5 text-foreground/80">
                Inputs used to compute resetAt
                <span className="ml-2 inline-block px-1.5 py-0 rounded text-[9px] uppercase tracking-wider bg-primary/15 text-primary border border-primary/30 align-middle">
                  Debug
                </span>
              </p>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                <dt className="opacity-70">Now (UTC, ISO)</dt>
                <dd className="text-foreground break-all tabular-nums">
                  {now.toISOString()}
                </dd>
                <dt className="opacity-70">Now (UTC ms)</dt>
                <dd className="text-foreground tabular-nums">
                  {now.getTime()}
                </dd>
                <dt className="opacity-70">UTC year / month</dt>
                <dd className="text-foreground tabular-nums">
                  {now.getUTCFullYear()} /{" "}
                  {String(now.getUTCMonth() + 1).padStart(2, "0")}{" "}
                  <span className="opacity-60">
                    (month index {now.getUTCMonth()})
                  </span>
                </dd>
                <dt className="opacity-70">Cycle boundary day</dt>
                <dd className="text-foreground">1st of next month, 00:00 UTC</dd>
                <dt className="opacity-70">Formula</dt>
                <dd className="text-foreground break-all">
                  <code className="opacity-90">
                    Date.UTC({now.getUTCFullYear()},{" "}
                    {now.getUTCMonth() + 1}, 1, 0, 0, 0)
                  </code>
                </dd>
                <dt className="opacity-70">→ resetAt (UTC ms)</dt>
                <dd className="text-foreground tabular-nums">
                  {resetAt.getTime()}
                </dd>
                <dt className="opacity-70">→ resetAt (ISO)</dt>
                <dd className="text-foreground break-all tabular-nums">
                  {resetAt.toISOString()}
                </dd>
                <dt className="opacity-70">msUntilReset</dt>
                <dd className="text-foreground tabular-nums">
                  {msUntilReset.toLocaleString()} ms
                </dd>
                {/* breakdownUntilReset — derived d/h/m view of the same
                    msUntilReset above, sourced from the shared helper in
                    `lib/creditReset` so the debug panel and the headline
                    countdown can never disagree. */}
                {(() => {
                  const bd = breakdownUntilReset(msUntilReset);
                  return (
                    <>
                      <dt className="opacity-70">breakdownUntilReset</dt>
                      <dd className="text-foreground tabular-nums">
                        <code className="opacity-90">
                          {`{ days: ${bd.days}, hours: ${bd.hours}, minutes: ${bd.minutes} }`}
                        </code>
                      </dd>
                      <dt className="opacity-70">→ compact</dt>
                      <dd className="text-foreground tabular-nums">
                        {bd.days}d {bd.hours}h {bd.minutes}m
                      </dd>
                    </>
                  );
                })()}
              </dl>
            </div>
            )}
            <p className="mt-2 opacity-70">
              Credits refill at 00:00 UTC on the 1st of each month.
              Daily caps reset at 00:00 UTC daily.
            </p>
          </details>
        )}
        <div className="mt-6 flex flex-col gap-2">
          <Link
            to="/account"
            className="inline-flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-xs font-mono hover:bg-secondary/60 transition-colors"
          >
            <span>Open full billing in Account</span>
            <ExternalLink className="w-3.5 h-3.5 opacity-60" />
          </Link>
          <Link
            to="/usage-limits"
            className="inline-flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-xs font-mono hover:bg-secondary/60 transition-colors"
          >
            <span>How limits &amp; pricing work</span>
            <ExternalLink className="w-3.5 h-3.5 opacity-60" />
          </Link>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default InShellUsageMeter;